import React, { useState, useEffect, useCallback } from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { NotificationContext } from "./NotificationContext";
import { jwtDecode } from "jwt-decode";
import { Home } from "./Pages/home";
import Billing from "./Pages/billing";
import { Bookings } from "./Pages/bookings";
import Invoices from "./Pages/invoices";
import { Patients } from "./Pages/patients";
import { Patientsappointment } from "./Pages/patientsappointment";
import { NewPatientsappointment } from "./Pages/Newpatientsappointment";
import { Reports } from "./Pages/reports";
import { Login } from "./Pages/landingpage";
import { Layout } from "./Layout";
import { Settings } from "./Pages/settings";
import { Userinfo } from "./Pages/userinfo";
import { Patientinfo } from "./Pages/patientinfo";
import { Practice } from "./Pages/practice";
import { Arrived } from "./Pages/checkIn";
import { UserProfile } from "./Pages/userprofile";
import { ForgotPassword } from "./Pages/forgotPassword";
import { Accounts } from "./Pages/Accounts";
import { PostedInvoice } from "./Pages/postedInvoices";
import { PaymentPage } from "./Pages/paymentpage";
import { OnlineBooking } from "./Pages/onlinebooking";
import { IntakeForm } from "./Pages/intakeForm";
import { IntakeReviews } from "./Pages/intakeReviews";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import BillingERA from "./Pages/billingERA";
import Session from "./Components/Sessions/Session";
import RecordingProvider from "./Components/SyncTo/RecordingProvider";
import { purgeAll as purgeSyncToIDB } from "./Components/SyncTo/hooks/useSyncToIDB";
import { DataMigration } from "./Pages/dataMigration";

import axios from "axios";
import "./custom.scss";

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002";

// 🔐 Utility to logout and redirect.
// Phase 30 — PITFALLS #6: purge SyncTo IndexedDB rows before clearing the
// session so recordings cannot leak across users on a shared workstation.
// Fire-and-forget (idb handles mid-redirect cleanup gracefully); do NOT await
// because this helper is called synchronously from multiple sites.
function handleLogout() {
  try { purgeSyncToIDB().catch(() => {}); } catch {}
  sessionStorage.removeItem("user");
  window.location.href = "/#/login";
}

// 🔐 Enhanced PrivateRoute to check expiry too
function PrivateRoute({ children }) {
  const token = sessionStorage.getItem("user");
  const location = useLocation();

  if (!token) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(
          location.pathname + location.search,
        )}`}
        replace
      />
    );
  }

  try {
    const decoded = jwtDecode(token);
    const isExpired = decoded.exp * 1000 < Date.now();
    if (isExpired) {
      handleLogout();
      return null;
    }
  } catch (err) {
    console.error("Invalid token format");
    handleLogout();
    return null;
  }

  return children;
}

// 🤖 Component wrapper for routes that need reCAPTCHA protection
function PublicRouteWithCaptcha({ children }) {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
      scriptProps={{
        async: false,
        defer: false,
        appendTo: "head",
      }}
    >
      {children}
    </GoogleReCaptchaProvider>
  );
}

function App() {
  useEffect(() => {
    const token = sessionStorage.getItem("user");

    if (token) {
      axios.defaults.headers.common["authorization"] = `Bearer ${token}`;

      try {
        const decoded = jwtDecode(token);
        const expirationTime = decoded.exp * 1000;
        const now = Date.now();
        if (expirationTime <= now) {
          handleLogout(); // token already expired
        } else {
          // Set a timer to log out when the token expires
          const timeout = setTimeout(() => {
            handleLogout();
          }, expirationTime - now);

          return () => clearTimeout(timeout); // cleanup if component unmounts
        }
      } catch (err) {
        console.error("Error decoding token:", err);
        handleLogout();
      }
    }
  }, []);

  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((notification) => {
    const newNotification = {
      ...notification,
      id: notification.id || notification._id || Date.now() + Math.random(),
      isRead: notification.isRead !== undefined ? notification.isRead : false,
      timestamp: notification.timestamp || Date.now(),
    };

    setNotifications((prev) => {
      const key = newNotification._id || newNotification.id;
      // If the same notification is already in state (from API fetch or earlier socket
      // event), do not add a second copy.
      if (key && prev.some((n) => (n._id || n.id) === key)) {
        return prev;
      }
      return [newNotification, ...prev];
    });
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      // Update in database
      await axios.put(`${URL}/notifications/${notificationId}/read`);

      // Update local state
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId ||
          notification._id === notificationId
            ? { ...notification, isRead: true }
            : notification,
        ),
      );
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      // Still update locally if API call fails
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId ||
          notification._id === notificationId
            ? { ...notification, isRead: true }
            : notification,
        ),
      );
    }
  }, []);

  const removeNotification = useCallback(async (notificationId) => {
    try {
      // Remove from database
      await axios.delete(`${URL}/notifications/${notificationId}`);

      // Update local state
      setNotifications((prev) =>
        prev.filter(
          (notification) =>
            notification.id !== notificationId &&
            notification._id !== notificationId &&
            notification.downloadId !== notificationId,
        ),
      );
    } catch (error) {
      console.error("Failed to remove notification:", error);
      // Still remove locally if API call fails
      setNotifications((prev) =>
        prev.filter(
          (notification) =>
            notification.id !== notificationId &&
            notification._id !== notificationId &&
            notification.downloadId !== notificationId,
        ),
      );
    }
  }, []);

  const clearAllNotifications = useCallback(async () => {
    try {
      await axios.delete(`${URL}/notifications`);
      setNotifications([]);
    } catch (error) {
      console.error("Failed to clear notifications:", error);
      // Still clear locally if API call fails
      setNotifications([]);
    }
  }, []);

  return (
    <NotificationContext.Provider value={{ addNotification }}>
      <RecordingProvider>
        <Router>
          <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/arrived/:practiceNumbers" element={<Arrived />} />
        <Route
          path="/onlinebooking/:id"
          element={
            <PublicRouteWithCaptcha>
              <OnlineBooking />
            </PublicRouteWithCaptcha>
          }
        />
        <Route path="/reset-password" element={<ForgotPassword />} />
        <Route
          path="/intake/:tokenId"
          element={
            <PublicRouteWithCaptcha>
              <IntakeForm />
            </PublicRouteWithCaptcha>
          }
        />

        <Route path="/payment/:practiceId/:status" element={<PaymentPage />} />

        {/* Authenticated Routes */}
        <Route
          element={
            <Layout
              notifications={notifications}
              addNotification={addNotification}
              removeNotification={removeNotification}
              markAsRead={markAsRead}
              setNotifications={setNotifications}
              clearAllNotifications={clearAllNotifications}
            />
          }
        >
          <Route
            path="/home"
            element={
              <PrivateRoute>
                <Home />
              </PrivateRoute>
            }
          />
          <Route
            path="/practice"
            element={
              <PrivateRoute>
                <Practice />
              </PrivateRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <PrivateRoute>
                <Billing />
              </PrivateRoute>
            }
          />
          <Route
            path="/billing/era/:id"
            element={
              <PrivateRoute>
                <BillingERA />
              </PrivateRoute>
            }
          />
          <Route
            path="/bookings"
            element={
              <PrivateRoute>
                <Bookings />
              </PrivateRoute>
            }
          />
          <Route
            path="/patients/appointment/:id/:appointmentId"
            element={
              <PrivateRoute>
                <Patientsappointment />
              </PrivateRoute>
            }
          />
          <Route
            path="/patients/session/:id/:sessionId"
            element={
              <PrivateRoute>
                <Patientsappointment />
              </PrivateRoute>
            }
          />
          <Route
            path="/newpatients/session/:appointmentId"
            element={
              <PrivateRoute>
                <NewPatientsappointment />
              </PrivateRoute>
            }
          />
          <Route
            path="/patients/:id"
            element={
              <PrivateRoute>
                <Patients />
              </PrivateRoute>
            }
          />
          <Route
            path="/accounts/:id"
            element={
              <PrivateRoute>
                <Accounts />
              </PrivateRoute>
            }
          />
          <Route
            path="/invoices/:id/:accountId"
            element={
              <PrivateRoute>
                <Invoices />
              </PrivateRoute>
            }
          />
          <Route
            path="/invoices/posted/:id/"
            element={
              <PrivateRoute>
                <PostedInvoice />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute>
                <Reports />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />
          <Route
            path="/userinfo/:id"
            element={
              <PrivateRoute>
                <Userinfo />
              </PrivateRoute>
            }
          />
          <Route
            path="/patientinfo/:id"
            element={
              <PrivateRoute>
                <Patientinfo />
              </PrivateRoute>
            }
          />
          <Route
            path="/userprofile"
            element={
              <PrivateRoute>
                <UserProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="/intake-reviews"
            element={
              <PrivateRoute>
                <IntakeReviews />
              </PrivateRoute>
            }
          />
          <Route
            path="/datamigration"
            element={
              <PrivateRoute>
                <DataMigration />
              </PrivateRoute>
            }
          />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </RecordingProvider>
    </NotificationContext.Provider>
  );
}

export default App;
